import CustomAutoComplete, {
  Option,
} from "@/modules/common/components/input/dropdowns/auto-complete";
import CommonBottomSheet from "@/modules/common/components/ui-kit/bottom-sheets/common-bottom-sheet";
import ModalHeader from "@/modules/common/components/ui-kit/modals/modal-header";
import ModalFooter from "@/modules/common/components/ui-kit/modals/modal-footer";
import {
  TOAST_IDS,
  ToastType,
  PermissionLevel,
  PermissionsEnum,
} from "@/modules/common/types";
import { showToast, userHasPermission } from "@/modules/common/utils";
import { useLeadContext } from "@/modules/lead-page/context/lead-page.context";
import { editJourneyStatus } from "@/modules/lead-page/api/lead-page.v2.api";
import { getJourneyStatuses } from "@/modules/leads/api/index.v2";
import { queryCommonFlags } from "@/api/constants";
import {
  Box,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useContext, useEffect, useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/queryClient";
import { NawyPassportContext } from "@/modules/common/context/nawy-passport.provider";
import { UpdateJourneyStatusRequest } from "@/modules/common/dtos/index.v2";
import { useForm, Controller } from "react-hook-form";
import EditAssignedUser from "@/modules/lead-page/components/lead-info/edit-fields/assigned-user";
import useEditAssignedUser from "@/modules/lead-page/hooks/lead-info/edit-assigned-user.hook";
import LeadAgentEditable from "@/modules/property-tracks/components/lead-status/lead-agent-editable";
import FieldWrapper from "@/modules/lead-page/new/components/sidebar/fields/field-wrapper";
import CopyButton from "@/modules/common/components/ui/copy-button";
import { PROPERTY_TRACKS } from "@/modules/property-tracks/utils";

function TrackLeadStatusMobile() {
  const theme = useTheme();
  const { lead, isAssignedAgent } = useLeadContext();
  const { assignedUser, toggleEditAssignedUserModal } = useEditAssignedUser({
    leadRow: lead?.data?.lead,
  });

  const { permissions: currentUserPermissions } =
    useContext(NawyPassportContext);

  const preventEditStatus = userHasPermission(
    { name: PermissionsEnum.PREVENT_EDIT_STATUS, level: PermissionLevel.WRITE },
    currentUserPermissions
  );

  const journeyUId = lead?.data?.lead?.journey_uid;
  const currentStatus = lead?.data.lead.status;

  const [modalOpen, setModalOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<any>({
    defaultValues: {
      status: "",
      comment: "",
    },
  });

  const { data: statusesData, isLoading } = useQuery({
    queryKey: ["getJourneyStatuses", journeyUId],
    queryFn: () =>
      journeyUId ? getJourneyStatuses(journeyUId) : Promise.resolve(null),
    ...queryCommonFlags,
    enabled: !!journeyUId,
  });

  const currentStatusOption = useMemo(() => {
    if (!currentStatus || !statusesData?.data) return null;

    const foundStatus = statusesData.data.find(
      (s) => s.name === currentStatus.status
    );

    return foundStatus || null;
  }, [currentStatus, statusesData]);

  useEffect(() => {
    if (modalOpen && currentStatusOption) {
      reset({
        status: currentStatusOption.uid,
        comment: "",
      });
    }
  }, [modalOpen, currentStatusOption, reset]);

  const editJourneyStatusMutation = useMutation({
    mutationFn: (update: UpdateJourneyStatusRequest) =>
      editJourneyStatus(journeyUId, update),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getLead"] });
      queryClient.invalidateQueries({ queryKey: ["getComments"] });
      queryClient.invalidateQueries({ queryKey: ["getStagesByJourney"] });
      queryClient.invalidateQueries({ queryKey: ["getStatuses"] });

      setDisabled(false);
      setModalOpen(false);
      reset();
      showToast({
        type: ToastType.SUCCESS,
        message: "Lead Status updated successfully",
        toastId: TOAST_IDS.EDIT_LEAD_SUCCESS,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      setDisabled(false);
      showToast({
        type: ToastType.ERROR,
        message:
          error?.response?.data?.message ??
          "Failed to update lead status. Please try again.",
        toastId: TOAST_IDS.EDIT_LEAD_ERROR,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
    },
  });

  const handleOpenModal = () => {
    if (preventEditStatus && !isAssignedAgent) {
      return;
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    reset();
  };

  const onSubmit = (data: any) => {
    if (!journeyUId) {
      showToast({
        type: ToastType.ERROR,
        message: "Journey UID not found",
        toastId: TOAST_IDS.EDIT_LEAD_ERROR,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
      return;
    }

    setDisabled(true);
    editJourneyStatusMutation.mutate({
      statusUId: data.status,
      comment: data.comment.trim(),
    });
  };

  // Filter statuses - exclude those with forms
  const availableStatuses = useMemo(() => {
    if (!statusesData?.data) return [];
    return statusesData.data;
  }, [statusesData]);

  const displayStatusOption: Option | null = useMemo(() => {
    if (!currentStatusOption) return null;
    return {
      label: currentStatusOption.name,
      value: currentStatusOption.uid,
      color: currentStatusOption.color || "#000",
    };
  }, [currentStatusOption]);

  const isDisabled =
    disabled ||
    editJourneyStatusMutation.isPending ||
    (preventEditStatus && !isAssignedAgent);

  return (
    <Box
      sx={(t) => ({
        border: `1px solid ${t.nawy.colors.semantic.border.secondary}`,
        backgroundColor: "white",
        borderRadius: t.nawy.radius.xl,
      })}
    >
      <Box sx={(t) => ({ px: t.nawy.spacing.md, pt: t.nawy.spacing.xs, pb: "4px", display: "flex", alignItems: "center", justifyContent: "space-between" })}>
        <Typography
          variant="inherit"
          sx={{ fontSize: "15px", fontWeight: 600, lineHeight: "22px" }}
        >
          Lead Status
        </Typography>
        {lead?.data?.lead?.customer_uid &&
          lead.data.lead_type.lead_type ===
            PROPERTY_TRACKS.FINISHING.toLowerCase() && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                sx={(t) => ({
                  ...t.nawy.typography.english.variants.body.sm.regular,
                  color: t.nawy.colors.semantic.text.secondary,
                })}
              >
                Customer ID
              </Typography>
              <CopyButton
                text={lead?.data?.lead?.customer_uid || ""}
                color={theme.nawy.colors.semantic.layer.accent}
                size={14}
              />
            </Box>
          )}
      </Box>

      <Stack sx={(t) => ({ px: t.nawy.spacing.md, pt: "4px", pb: t.nawy.spacing.xs })} gap={1}>
        {/* One-row split: status pill + assigned agent */}
        <Stack direction="row" gap={1} alignItems="stretch">
          <Tooltip title={displayStatusOption?.label ?? ""} placement="top" arrow>
            <Box
              onClick={(e) => {
                e.stopPropagation();
                handleOpenModal();
              }}
              sx={(t) => ({
                flex: 1,
                minWidth: 0,
                display: "flex",
                cursor: isDisabled ? "not-allowed" : "pointer",
                "& > *": { width: "100%" },
                "& .MuiOutlinedInput-root": {
                  borderRadius: `${t.nawy.radius.lg} !important`,
                  "& fieldset": {
                    borderColor: `${t.nawy.colors.semantic.border.primary} !important`,
                  },
                },
              })}
            >
              <CustomAutoComplete
                disabled={isDisabled}
                options={availableStatuses.map((status) => ({
                  label: status.name,
                  value: status.uid,
                  color: status.color || "#000",
                }))}
                customPlaceholderSx={{
                  "& .MuiInputBase-input": {
                    textAlign: "center",
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    pointerEvents: "none",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    paddingRight: "28px !important",
                    fontSize: "13px",
                    fontFamily: "inherit",
                  },
                  "& .MuiOutlinedInput-root": {
                    pointerEvents: "none",
                  },
                }}
                loading={isLoading}
                value={displayStatusOption}
                placeholder="Choose a Status"
                disableClearable={true}
                onChange={() => {}}
              />
            </Box>
          </Tooltip>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <LeadAgentEditable
              onClose={() => toggleEditAssignedUserModal(false)}
              open={assignedUser.editAssignedUserModalOpen}
              title="Edit Assigned User"
              confirmButtonText="Save"
              cancelButtonText="Cancel"
              onConfirm={() => assignedUser.handleAssignLead()}
              isLoading={assignedUser.isLoading}
              isConfirmDisabled={!assignedUser.state}
              snapPoints={[1]}
              editable
              name={lead?.data?.lead?.user?.name}
              color={lead?.data?.lead?.user?.color}
              handleEdit={() => toggleEditAssignedUserModal(true)}
              sx={{ height: "100%" }}
            >
              <FieldWrapper>
                <EditAssignedUser assignedUser={assignedUser} lead={lead} />
              </FieldWrapper>
            </LeadAgentEditable>
          </Box>
        </Stack>

      </Stack>

      <CommonBottomSheet
        isOpen={modalOpen}
        onClose={handleCloseModal}
        snapPoints={[0.5]}
      >
        <ModalHeader title="Edit Status" onClose={handleCloseModal} />

        <Stack gap={2} width="100%" minHeight="fit-content" p={2}>
          <Controller
            name="status"
            control={control}
            rules={{
              required: "Status is required",
            }}
            render={({ field }) => (
              <CustomAutoComplete
                label="Status"
                placeholder="Select a status"
                options={availableStatuses.map((status) => ({
                  label: status.name,
                  value: status.uid,
                  color: status.color || "#000",
                }))}
                value={
                  field.value
                    ? availableStatuses
                        .map((status) => ({
                          label: status.name,
                          value: status.uid,
                          color: status.color || "#000",
                        }))
                        .find((opt) => opt.value === field.value) || null
                    : null
                }
                onChange={(selected) => {
                  if (selected && !Array.isArray(selected)) {
                    field.onChange(selected.value);
                  } else {
                    field.onChange("");
                  }
                }}
                loading={isLoading}
                disabled={disabled}
                fullWidth={true}
                error={!!errors.status}
                helperText={errors.status?.message as string}
              />
            )}
          />

          <Controller
            name="comment"
            control={control}
            rules={{
              required: "Comment is required",
              minLength: {
                value: 4,
                message: "Minimum 4 characters required",
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Comment"
                multiline
                rows={4}
                disabled={disabled}
                required
                error={!!errors.comment}
                helperText={errors.comment?.message as string}
              />
            )}
          />
        </Stack>

        <Box position="sticky" bottom={0}>
          <ModalFooter
            confirmButtonText="Save"
            cancelButtonText="Cancel"
            onConfirm={handleSubmit(onSubmit)}
            onCancel={handleCloseModal}
            isLoading={disabled}
          />
        </Box>
      </CommonBottomSheet>
    </Box>
  );
}

export default TrackLeadStatusMobile;
